import * as R from 'ramda'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import * as EmotionTheming from 'emotion-theming'
import * as Urql from 'urql'
import PropTypes from 'prop-types'
import kebabCase from 'lodash.kebabcase'

import * as Common from '@rushplay/common'
import * as I18n from '@rushplay/i18n'
import css from '@styled-system/css'
import styled from '@emotion/styled'

import * as Icons from '../icons'
import { Button } from '../button'
import { HtmlContent } from '../html-content'
import { InformativeSection } from '../informative-section'
import { WelcomeOfferGameInfo } from '../welcome-offer-game-info'
import { useSafeUpdateQuery } from '../use-safe-update-query'
import { useTranslationImgproxyUrl } from '../use-imgproxy-url'

const getFeaturedLandingGames = `
query FeaturedLandingGames {
  catalog(key: "featured-landing-game") {
    ... on GetCatalogSuccess {
      kind
      categories {
        id
        sections {
          id
          key
          layout
          tileSize
          totalGamesCount
          games {
            backgroundUrl
            displayProvider
            id
            key
            provider
            title
            titleUrl
            jackpotValue
            feature
            rtp
            volatility
            biggestWinCents
            minBetCents
            maxBetCents
            dealerName
            seatsTotal
            seatsAvailable
            betBehind
            releaseDate
          }
        }
      }
    }
    ... on GetCatalogFailure {
      error
      errorCode
    }
  }
}
`

const StyledSvgText = styled.text`
  ${props =>
    css({
      fontSize: props.fontSize,
      filter: props.filter,
    })}

  @media screen and (max-width: 1200px) {
    ${props =>
      css({
        fontSize: props.mediaFontSize,
        filter: props.filter ? props.mediaFilter : null,
      })}
  }
`

const StyledSvg = styled.svg`
  height: 69px;
  width: 218px;
  z-index: 2;

  @media screen and (max-width: 1200px) {
    width: 110px;
    height: 35px;
  }
`

export function LandingPage(props) {
  const i18n = I18n.useI18n()
  const loginQuery = useSafeUpdateQuery({ login: 'me' })
  const registerQuery = useSafeUpdateQuery({ register: 'me' })

  const [response] = Urql.useQuery({
    query: getFeaturedLandingGames,
  })

  const imageUrlBonusMoney = useTranslationImgproxyUrl(
    i18n.translate(`landing-page.bonus-image.bonus-money`),
    {
      resizingWidth: '437',
    }
  )

  const imageUrlBonusFeature = useTranslationImgproxyUrl(
    i18n.translate(`landing-page.bonus-image.bonus-feature`),
    {
      resizingWidth: '437',
    }
  )

  const theme = EmotionTheming.useTheme()

  if (response.fetching) {
    return (
      <Common.Box
        display="flex"
        justifyContent="center"
        alignSelf="center"
        fontSize={['48px', '64px']}
        pt={3}
        width="100%"
      >
        <Icons.Spinner />
      </Common.Box>
    )
  }

  const game = R.path(
    ['data', 'catalog', 'categories', 0, 'sections', 0, 'games', 0],
    response
  )
  const splitedText = i18n.hasTranslation(
    'landing-page.feature-bonus.free-spins'
  )
    ? i18n.translate('landing-page.feature-bonus.free-spins').split(' ')
    : null

  return (
    <Common.Box display="flex" flexDirection="column" width="100%">
      <Common.Box
        backgroundImage={`url(${theme['bg-pattern-default']}), linear-gradient(180deg, rgba(0, 87, 255,0.3), rgba(23, 6, 29, 1)),linear-gradient(180deg, rgba(73, 176, 249, 1), rgba(23, 6, 29, 0.2))`}
        backgroundSize="auto, center, center"
        backgroundPosition="left, center, center"
      >
        <Common.Box
          pt={['10px', null, null, '85px']}
          display="flex"
          justifyContent="space-between"
          flexDirection={['column', null, null, 'row']}
          maxWidth={['350px', null, null, '955px']}
          mx="auto"
        >
          <Common.Box alignSelf="flex-start" position="relative">
            <Common.Box
              as="img"
              src={imageUrlBonusMoney}
              alt="Bonus-Money"
              width={['219px', null, null, '437px']}
              minHeight={['229.5px', null, null, '457px']}
            />
            <Common.Box
              position="absolute"
              top={['15%', null, null, '22%']}
              left="0px"
              width="100%"
              textAlign="center"
              fontWeight="bold"
              fontSize={[7, null, null, '52px']}
              lineHeight="41px"
            >
              {props.bonusPercentage}%
            </Common.Box>
            <Common.Box
              position="absolute"
              top="58%"
              left="0px"
              width="100%"
              textAlign="center"
              fontWeight="600"
              fontSize={[4, null, null, '28px']}
              lineHeight={['27px', null, null, '42px']}
            >
              {i18n.translate('landing-page.money-bonus.up-to', {
                amountCents: props.maxBonusAmount,
              })}
            </Common.Box>
          </Common.Box>
          <Common.Box
            borderRadius="9999px"
            height="58px"
            width="58px"
            backgroundColor="secondaryButton"
            color="g-text"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink="0"
            alignSelf="center"
            mx="-1em"
            mt={['-4em', null, null, '-5em']}
            mb={['-2em', null, null, '0px']}
            zIndex="1"
          >
            <Common.Box fontSize="23px">
              <Icons.Plus />
            </Common.Box>
          </Common.Box>
          <Common.Box alignSelf="flex-end" position="relative">
            <Common.Box
              as="img"
              src={imageUrlBonusFeature}
              alt="Bonus-Feature"
              width={['219px', null, null, '437px']}
              minHeight={['229.5px', null, null, '457px']}
            />
            <Common.Box
              position="absolute"
              top="8%"
              fontWeight="700"
              width="100%"
            >
              <Common.Box display="flex" justifyContent="center">
                <StyledSvg>
                  <defs>
                    <linearGradient id="gold" x1="0%" y1="20%" x2="0%" y2="70%">
                      <stop stopColor="#fef414" offset="0%" />
                      <stop stopColor="#f9960c" offset="100%" />
                    </linearGradient>
                    <filter id="no_shadow">
                      <feDropShadow dx="0" dy="0" />
                    </filter>
                    <filter id="shadow_1">
                      <feDropShadow
                        dx="1"
                        dy="2"
                        stdDeviation="1"
                        floodColor="#AE3054"
                      />
                    </filter>
                    <filter id="shadow_2">
                      <feDropShadow
                        dx="2"
                        dy="4"
                        stdDeviation="1"
                        floodColor="#AD3053"
                      />
                    </filter>
                    <filter id="shadow_3">
                      <feDropShadow
                        dx="1"
                        dy="1"
                        stdDeviation="1"
                        floodColor="#AE3054"
                      />
                    </filter>
                    <filter id="shadow_4">
                      <feDropShadow
                        dx="2"
                        dy="2"
                        stdDeviation="1"
                        floodColor="#AE3054"
                      />
                    </filter>
                  </defs>
                  <StyledSvgText
                    x="96%"
                    y="0"
                    fontSize="76px"
                    mediaFontSize="36px"
                    fill="url(#gold)"
                    filter="url(#shadow_2)"
                    mediaFilter="url(#shadow_1)"
                    stroke="rgba(77, 20, 1, 0.5)"
                    strokeWidth="2"
                    letterSpacing="-3"
                    dominantBaseline="hanging"
                    textAnchor="end"
                  >
                    {props.maxFixedFreespins}
                  </StyledSvgText>
                  <StyledSvgText
                    x="96%"
                    y="0"
                    fontSize="76px"
                    mediaFontSize="36px"
                    fill="url(#gold)"
                    stroke="rgba(77, 20, 1, 0.5)"
                    strokeWidth="1"
                    letterSpacing="-3"
                    dominantBaseline="hanging"
                    textAnchor="end"
                  >
                    {props.maxFixedFreespins}
                  </StyledSvgText>
                </StyledSvg>
                {splitedText && (
                  <StyledSvg>
                    <StyledSvgText
                      x="0"
                      y="0"
                      fontSize="35px"
                      mediaFontSize="17px"
                      fill="url(#gold)"
                      filter="url(#shadow_4)"
                      mediaFilter="url(#shadow_3)"
                      stroke="rgba(77, 20, 1, 0.5)"
                      strokeWidth="2"
                      dominantBaseline="hanging"
                    >
                      {splitedText[0]}
                    </StyledSvgText>
                    <StyledSvgText
                      x="0"
                      y="0"
                      fontSize="35px"
                      mediaFontSize="17px"
                      fill="url(#gold)"
                      stroke="rgba(77, 20, 1, 0.5)"
                      strokeWidth="1"
                      dominantBaseline="hanging"
                    >
                      {splitedText[0]}
                    </StyledSvgText>

                    <StyledSvgText
                      x="0"
                      y="90%"
                      fontSize="35px"
                      mediaFontSize="17px"
                      fill="url(#gold)"
                      filter="url(#shadow_4)"
                      mediaFilter="url(#shadow_3)"
                      stroke="rgba(77, 20, 1, 0.5)"
                      strokeWidth="2"
                    >
                      {splitedText[1]}
                    </StyledSvgText>
                    <StyledSvgText
                      x="0"
                      y="90%"
                      fontSize="35px"
                      mediaFontSize="17px"
                      fill="url(#gold)"
                      stroke="rgba(77, 20, 1, 0.5)"
                      strokeWidth="1"
                    >
                      {splitedText[1]}
                    </StyledSvgText>
                  </StyledSvg>
                )}
              </Common.Box>
              <Common.Box height={['10px', null, null, '20px']}>
                <svg width="100%" height="100%">
                  <StyledSvgText
                    x="43%"
                    y="70%"
                    fontSize="20px"
                    mediaFontSize="12px"
                    fill="url(#gold)"
                    filter="url(#shadow_4)"
                    mediaFilter="url(#shadow_3)"
                    stroke="rgba(77, 20, 1, 0.5)"
                    strokeWidth="2"
                  >
                    {i18n.translate('landing-page.feature-bonus.on')}
                  </StyledSvgText>
                  <StyledSvgText
                    x="43%"
                    y="70%"
                    fontSize="20px"
                    mediaFontSize="12px"
                    fill="url(#gold)"
                    stroke="rgba(77, 20, 1, 0.5)"
                    strokeWidth="1"
                  >
                    {i18n.translate('landing-page.feature-bonus.on')}
                  </StyledSvgText>
                </svg>
              </Common.Box>
            </Common.Box>
          </Common.Box>
        </Common.Box>

        <Common.Box pt={3} width="327px" mx="auto" textAlign="center">
          <ReactRouter.Link
            to={`?${registerQuery}`}
            data-testid="landing-page.register-button"
          >
            <Button variant="primary" stretch>
              <Common.Box
                width="100%"
                fontSize="24px"
                lineHeight="16px"
                fontWeight="800"
                justifyContent="center"
                py="0"
              >
                {i18n.translate('landing-page.register')}
              </Common.Box>
              <Common.Box
                fontSize="14px"
                lineHeight="16px"
                fontWeight="600"
                pt="4px"
              >
                {i18n.translate('landing-page.register.sub-header')}
              </Common.Box>
            </Button>
          </ReactRouter.Link>
          <Common.Text
            as={ReactRouter.Link}
            to={`?${loginQuery}`}
            data-testid="landing-page.login-button"
            textDecoration="underline"
          >
            <Common.Box fontSize="16px" mt="11px" fontWeight="700">
              {i18n.translate('landing-page.login.cta')}
            </Common.Box>
          </Common.Text>
        </Common.Box>
      </Common.Box>
      {game && (
        <Common.Box pt="33px" maxWidth="100%" mx="auto" minHeight="480px">
          <WelcomeOfferGameInfo
            gameId={game.id}
            backgroundUrl={game.backgroundUrl}
            description={`game-info.${kebabCase(
              game.displayProvider || game.provider
            )}.${kebabCase(game.key)}.description`}
            gameKey={game.key}
            provider={game.provider}
            title={game.title}
            titleUrl={game.titleUrl}
            jackpotValue={game.jackpotValue}
            feature={game.feature}
            rtp={game.rtp}
            volatility={game.volatility}
            biggestWinCents={game.biggestWinCents}
            minBetCents={game.minBetCents}
            maxBetCents={game.maxBetCents}
            dealerName={game.dealerName}
            seatsTotal={game.seatsTotal}
            seatsAvailable={game.seatsAvailable}
            betBehind={game.betBehind}
            releaseDate={game.releaseDate}
          />
        </Common.Box>
      )}
      <Common.Box maxWidth="1080px" alignSelf="center" py={[2, 6]}>
        <InformativeSection />
      </Common.Box>
      <Common.Box
        textAlign="center"
        pt="6"
        pb="8"
        fontSize={[6, null, 7]}
        fontWeight="600"
      >
        {i18n.translate('landing-page.boom.expiriance')}
      </Common.Box>
      <Common.Box
        p={2}
        maxWidth="1080px"
        mx="auto"
        textAlign="left"
        fontSize={2}
      >
        <HtmlContent
          html={{
            __html: i18n.translate('landing-page.seo-content'),
          }}
        />
      </Common.Box>
    </Common.Box>
  )
}

LandingPage.propTypes = {
  bonusPercentage: PropTypes.number.isRequired,
  maxBonusAmount: PropTypes.number.isRequired,
  maxFeatureTriggers: PropTypes.number.isRequired,
  maxFixedFreespins: PropTypes.number.isRequired,
}

// for @loadable/components
export default LandingPage
